import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { ExpenseNotificationCenter } from "@/components/notifications/ExpenseNotificationCenter";
import { UserButton } from "@/components/auth/UserButton";
import { Button } from "@/components/ui/button";
import { InviteUserButton } from "@/components/user/InviteUserButton";
import { OnboardingButton } from "@/components/onboarding/OnboardingButton";
import { SetupGuideDialog } from "@/components/setup-guide/SetupGuideDialog";
import { WorkspaceSwitcher } from "@/components/workspace/WorkspaceSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Settings2, Sun, Moon, Monitor, Wand2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/components/ThemeProvider";
import { useSetupGuide } from "@/hooks/useSetupGuide";
import { useAuth } from "@/hooks/useAuth";

interface HeaderActionsProps {
  authenticated: boolean;
}

export function HeaderActions({ authenticated }: HeaderActionsProps) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { showSetupGuide, loading: setupLoading } = useSetupGuide();

  // Setup guide is only opened manually by user clicking the button

  if (!authenticated) {
    return (
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/login">Log in</Link>
        </Button>
        <Button asChild size="sm">
          <Link to="/register">Sign up</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {/* Quick Settings Dropdown - Groups less frequently used items */}
      <TooltipProvider>
        <Tooltip>
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Settings2 className="h-4 w-4" />
                  <span className="sr-only">{t('header.quickSettings', 'Quick Settings')}</span>
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('header.quickSettings', 'Quick Settings')}</p>
            </TooltipContent>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>{t('header.quickSettings', 'Quick Settings')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {/* Theme Selection */}
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              {t('header.theme', 'Theme')}
            </DropdownMenuLabel>
            <div className="px-2 py-1.5">
              <div className="flex gap-1">
                <Button
                  variant={theme === 'light' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="flex-1 h-8 text-xs gap-1.5"
                  onClick={() => setTheme('light')}
                >
                  <Sun className="h-3.5 w-3.5" />
                  {t('theme.light', 'Light')}
                </Button>
                <Button
                  variant={theme === 'dark' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="flex-1 h-8 text-xs gap-1.5"
                  onClick={() => setTheme('dark')}
                >
                  <Moon className="h-3.5 w-3.5" />
                  {t('theme.dark', 'Dark')}
                </Button>
                <Button
                  variant={theme === 'system' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="flex-1 h-8 text-xs gap-1.5"
                  onClick={() => setTheme('system')}
                >
                  <Monitor className="h-3.5 w-3.5" />
                  {t('theme.system', 'Auto')}
                </Button>
              </div>
            </div>
            
            <DropdownMenuSeparator />
            
            {/* Setup Guide - Easy Setup */}
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setDropdownOpen(false); setTimeout(() => setSetupDialogOpen(true), 100); }}>
              <Wand2 className="mr-2 h-4 w-4" />
              {t('header.setupGuide', 'Easy Setup Guide')}
            </DropdownMenuItem>
            
            {/* Invite User */}
            <InviteUserButton asMenuItem />
            
            {/* Onboarding / Getting Started */}
            <OnboardingButton asMenuItem />
          </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>
      
      {/* Workspace Switcher */}
      <WorkspaceSwitcher />
      
      {/* Notifications - Always visible */}
      <ExpenseNotificationCenter />
      <NotificationCenter />
      
      {/* User Menu - Always visible */}
      <UserButton />
      
      {/* Setup Guide Dialog */}
      <SetupGuideDialog 
        open={setupDialogOpen} 
        onOpenChange={setSetupDialogOpen} 
      />
    </div>
  );
}
